// app/javascript/components/Department.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { t } from '../utils/i18n';
import Pagination from './Pagination';
import { toSomaliDept } from '../utils/deptTranslations';

const Department = ({ onBack, api }) => {
  const [departments, setDepartments] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [armyTypes, setArmyTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [subSearchTerm, setSubSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showSubForm, setShowSubForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [subCurrentPage, setSubCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [assignHeadCurrentPage, setAssignHeadCurrentPage] = useState(1);
  const [assignHeadItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    location_id: '',
    army_type_id: '',
    parent_id: ''
  });

  useEffect(() => {
    fetchDepartments();
    fetchPersonnel();
    fetchArmyTypes();
    fetchLocations();
  }, []);

  const fetchDepartments = async () => {
    try {
      const data = await api.get('/departments');
      setDepartments(data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchArmyTypes = async () => {
    try {
      const data = await api.get('/army_types');
      setArmyTypes(data);
    } catch (error) {
      console.error('Failed to fetch army types:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await api.get('/locations');
      setLocations(data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const data = await api.get('/users');
      const departmentHeads = data.users.filter(user =>
        user.roles?.some(role => role.name === 'department_head') ||
        user.role === 'department_head'
      );
      setPersonnel(departmentHeads);
    } catch (error) {
      console.error('Failed to fetch personnel:', error);
    }
  };

  const getParentDepartmentName = (parentId) => {
    if (!parentId) return 'Unknown';
    const parent = departments.find(dept => dept && dept.id === parentId);
    return parent ? toSomaliDept(parent.name) : 'Unknown';
  };

  // Filter main departments (no parent)
  const mainDepartments = useMemo(() => {
    const all = departments.filter(dept => dept && !dept.parent_id && dept.id);
    // Deduplicate by translated display name + army_type_id — keep the record with the lowest ID
    const seen = new Map();
    all.forEach(dept => {
      if (!dept || !dept.id) return;
      const normalizedName = toSomaliDept(dept.name || '').toLowerCase();
      const key = `${normalizedName}__${dept.army_type_id ?? ''}`;
      if (!seen.has(key) || dept.id < seen.get(key).id) {
        seen.set(key, dept);
      }
    });
    return Array.from(seen.values());
  }, [departments]);

  // Filter subdepartments (have parent)
  const subDepartments = useMemo(() => {
    const all = departments.filter(dept => dept && dept.parent_id && dept.id);
    // Deduplicate by translated display name + army_type_id + translated parent name
    const seen = new Map();
    all.forEach(dept => {
      if (!dept || !dept.id) return;
      const normalizedName = toSomaliDept(dept.name || '').toLowerCase();
      const normalizedParentName = getParentDepartmentName(dept.parent_id).toLowerCase();
      const key = `${normalizedName}__${dept.army_type_id ?? ''}__${normalizedParentName}`;
      if (!seen.has(key) || dept.id < seen.get(key).id) {
        seen.set(key, dept);
      }
    });
    return Array.from(seen.values());
  }, [departments]);

  const filteredAndSortedDepartments = useMemo(() => {
    let filtered = mainDepartments.filter(dept => {
      const translatedName = toSomaliDept(dept.name || '').toLowerCase();
      return (
        dept.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        translatedName.includes(searchTerm.toLowerCase()) ||
        dept.army_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dept.head?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    const [field, order] = sortBy.split('-');
    return filtered.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      if (field === 'id') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [mainDepartments, searchTerm, sortBy]);

  const filteredAndSortedSubDepartments = useMemo(() => {
    let filtered = subDepartments.filter(dept => {
      const translatedName = toSomaliDept(dept.name || '').toLowerCase();
      const parentDept = departments.find(d => d.id === dept.parent_id);
      const parentEnglishName = (parentDept?.name || '').toLowerCase();
      const parentSomaliName = (parentDept ? toSomaliDept(parentDept.name) : '').toLowerCase();
      return (
        dept.name?.toLowerCase().includes(subSearchTerm.toLowerCase()) ||
        translatedName.includes(subSearchTerm.toLowerCase()) ||
        parentEnglishName.includes(subSearchTerm.toLowerCase()) ||
        parentSomaliName.includes(subSearchTerm.toLowerCase()) ||
        dept.army_type?.toLowerCase().includes(subSearchTerm.toLowerCase()) ||
        dept.head?.email?.toLowerCase().includes(subSearchTerm.toLowerCase())
      );
    });

    const [field, order] = sortBy.split('-');
    return filtered.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      if (field === 'id') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [subDepartments, subSearchTerm, sortBy, departments]);

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setDropdownOpen(false);
  };

  const handleAdd = () => {
    setFormData({ name: '', code: '', location_id: '', army_type_id: '', parent_id: '' });
    setEditingId(null);
    setShowForm(true);
    setShowSubForm(false);
  };

  const handleAddSub = () => {
    setFormData({ name: '', code: '', location_id: '', army_type_id: '', parent_id: '' });
    setEditingId(null);
    setShowForm(true);
    setShowSubForm(true);
  };

  const handleEdit = (department) => {
    setFormData({
      name: department.name || '',
      code: department.code || '',
      location_id: department.location_id || '',
      army_type_id: department.army_type_id || '',
      parent_id: department.parent_id || ''
    });
    setEditingId(department.id);
    setShowForm(true);
    setShowSubForm(!!department.parent_id);
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this department?')) {
      setLoading(true);
      try {
        await api.delete(`/departments/${id}`);
        setDepartments(departments.filter(dept => dept.id !== id));
      } catch (error) {
        console.error('Failed to delete department:', error);
        alert('Failed to delete department');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        const { response, data } = await api.put(`/departments/${editingId}`, { department: formData });
        if (response.ok) {
          setDepartments(departments.map(dept =>
            dept.id === editingId ? data.department : dept
          ));
        } else {
          alert('Failed to update department');
        }
      } else {
        const { response, data } = await api.post('/departments', { department: formData });
        if (response.ok) {
          setDepartments([...departments, data.department]);
        } else {
          alert('Failed to create department');
        }
      }
      setShowForm(false);
      setShowSubForm(false);
      setFormData({ name: '', code: '', location_id: '', army_type_id: '', parent_id: '' });
      setEditingId(null);
      fetchDepartments();
    } catch (error) {
      console.error('Failed to save department:', error);
      alert('Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setShowSubForm(false);
    setFormData({ name: '', code: '', location_id: '', army_type_id: '', parent_id: '' });
    setEditingId(null);
  };

  const handleAssignHead = async (departmentId, newHeadId) => {
    try {
      const { response } = await api.put(`/departments/${newHeadId}/assign_head`, {
        department: departmentId
      });
      if (response.ok) {
        fetchDepartments();
      } else {
        alert('Failed to assign department head');
      }
    } catch (error) {
      console.error('Failed to assign head:', error);
      alert('Failed to assign department head');
    }
  };

  const paginatedDepartments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedDepartments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedDepartments, currentPage, itemsPerPage]);

  const paginatedSubDepartments = useMemo(() => {
    const startIndex = (subCurrentPage - 1) * itemsPerPage;
    return filteredAndSortedSubDepartments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedSubDepartments, subCurrentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedDepartments.length / itemsPerPage);
  const subTotalPages = Math.ceil(filteredAndSortedSubDepartments.length / itemsPerPage);

  const paginatedAssignHeadDepartments = useMemo(() => {
    const startIndex = (assignHeadCurrentPage - 1) * assignHeadItemsPerPage;
    return departments.slice(startIndex, startIndex + assignHeadItemsPerPage);
  }, [departments, assignHeadCurrentPage, assignHeadItemsPerPage]);

  const assignHeadTotalPages = Math.ceil(departments.length / assignHeadItemsPerPage);

  return (
    <>
      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb_40 baseTableWrap">
          <div className="dFlex baseTableHd mb_16">
            <h2 className="txt20 fwMedium colorBlack">
              {editingId ?
                (showSubForm ? t('edit_sub_department') : t('edit_department')) :
                (showSubForm ? t('add_sub_department') : t('add_department'))
              }
            </h2>
          </div>
          <form onSubmit={handleSubmit} style={{ padding: '24px 20px' }}>
            <div className="row">
              <div className="col-md-6 mb_20">
                <label className="mb_8">{showSubForm ? t('sub_department_name') : t('department_name')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('code')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  required
                />
              </div>
              {showSubForm && (
                <div className="col-md-6 mb_20">
                  <label className="mb_8">{t('department_name')}</label>
                  <select
                    className="form-control"
                    value={formData.parent_id}
                    onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
                    required
                  >
                    <option value="">Select Parent Department</option>
                    {mainDepartments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {toSomaliDept(dept.name)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('army_type')}</label>
                <select
                  className="form-control"
                  value={formData.army_type_id}
                  onChange={(e) => setFormData({...formData, army_type_id: e.target.value})}
                  required
                >
                  <option value="">Select Army Type</option>
                  {armyTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">{t('sidebar.location')}</label>
                <select
                  className="form-control"
                  value={formData.location_id}
                  onChange={(e) => setFormData({...formData, location_id: e.target.value})}
                  required
                >
                  <option value="">Select Location</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="dFlex mt_20">
              <button type="submit" className="btnBig me-3" disabled={loading}>
                {loading ? 'Saving...' : (editingId ? t('update') : t('add'))}
              </button>
              <button type="button" className="btnBig btnSecondary" onClick={handleCancel}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Department List */}
      <div className="mb_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">{t('department_list')} ({filteredAndSortedDepartments.length})</h2>
          <div className="dFlex baseTableTR">
            <button className="btnAdd me-3" onClick={handleAdd}>
              {t('add_department')}
            </button>
            <div className="searchBox">
              <input
                type="text"
                placeholder="Search by Name, Army Type, Head"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="button">
                <img src="/images/search1.png" alt="" />
              </button>
            </div>
            <div className="dropdown dropdownSortBy">
              <button
                className="dropdown-toggle"
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {t('sort_by')}
              </button>
              <ul className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('name-asc')}>Name A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('name-desc')}>Name Z-A</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('army_type-asc')}>Army Type A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('created_at-desc')}>Newest First</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable baseTableDepartment">
            <thead>
              <tr>
                <th>Department ID</th>
                <th>{t('department_name')}</th>
                <th>{t('army_type')}</th>
                <th>{t('department_head')}</th>
                <th>{t('created_date')}</th>
                <th style={{ minWidth: "175px" }}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDepartments.length > 0 ? (
                paginatedDepartments.map((department) => (
                  <tr key={department.id}>
                    <td>{department.id}</td>
                    <td>{toSomaliDept(department.name)}</td>
                    <td>{department.army_type}</td>
                    <td>{department?.head?.email || 'Not Assigned'}</td>
                    <td>{department.created_at ? new Date(department.created_at).toLocaleDateString() : ''}</td>
                    <td>
                      <div className="dFlex optBlock">
                        <button className="optBtn" onClick={() => handleEdit(department)} disabled={loading}>
                          <img src="/images/edit.png" alt="" />
                          <span>{t('edit')}</span>
                        </button>
                        <span className="bar">|</span>
                        <button className="optBtn" onClick={() => handleDelete(department.id)} disabled={loading}>
                          <img src="/images/delete.png" alt="" />
                          <span>{t('delete')}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>
                    No departments found matching "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage}/>
      </div>

      {/* Sub Department List */}
      <div className="mb_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">{t('sub_department_list')} ({filteredAndSortedSubDepartments.length})</h2>
          <div className="dFlex baseTableTR">
            <button className="btnAdd me-3" onClick={handleAddSub}>
              {t('add_sub_department')}
            </button>
            <div className="searchBox">
              <input
                type="text"
                placeholder="Search by Name, Army Type, Head"
                value={subSearchTerm}
                onChange={(e) => setSubSearchTerm(e.target.value)}
              />
              <button type="button">
                <img src="/images/search1.png" alt="" />
              </button>
            </div>
          </div>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable baseTableDepartment">
            <thead>
              <tr>
                <th>{t('sub_department_name')}</th>
                <th>{t('department_name')}</th>
                <th>{t('army_type')}</th>
                <th>{t('created_date')}</th>
                <th style={{ minWidth: "175px" }}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSubDepartments.length > 0 ? (
                paginatedSubDepartments.map((department) => {
                  if (!department || !department.id) return null;
                  return (
                    <tr key={department.id}>
                      <td>{toSomaliDept(department.name)}</td>
                      <td>{getParentDepartmentName(department.parent_id)}</td>
                      <td>{department.army_type}</td>
                      <td>{department.created_at ? new Date(department.created_at).toLocaleDateString() : ''}</td>
                      <td>
                        <div className="dFlex optBlock">
                          <button className="optBtn" onClick={() => handleEdit(department)} disabled={loading}>
                            <img src="/images/edit.png" alt="" />
                            <span>{t('edit')}</span>
                          </button>
                          <span className="bar">|</span>
                          <button className="optBtn" onClick={() => handleDelete(department.id)} disabled={loading}>
                            <img src="/images/delete.png" alt="" />
                            <span>{t('delete')}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>
                    No sub departments found matching "{subSearchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={subCurrentPage} totalPages={subTotalPages} onPageChange={setSubCurrentPage}/>
      </div>

      {/* Assign Department Heads */}
      <div className="baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">{t('assign_department_heads')}</h2>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable baseTableDepartment">
            <thead>
              <tr>
                <th>{t('department_name')}</th>
                <th>{t('current_head')}</th>
                <th>{t('assign_new_head')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAssignHeadDepartments.map((department) => (
                <tr key={`assign-${department.id}`}>
                  <td>{toSomaliDept(department.name)}</td>
                  <td>{department?.head?.email || 'Not Assigned'}</td>
                  <td>
                    <div className="formInpt">
                      <select
                        className="customSelect"
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAssignHead(department.id, e.target.value);
                          }
                        }}
                      >
                        <option value="">Select Department Head</option>
                        {personnel.map(person => (
                          <option key={person.id} value={person.id}>
                            {person.email} {person.first_name ? `- ${person.first_name} ${person.last_name}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="dFlex optBlock">
                      <button type="button" className="optBtn">
                        <img src="/images/save.png" alt="" />
                        <span>{t('save')}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={assignHeadCurrentPage} totalPages={assignHeadTotalPages} onPageChange={setAssignHeadCurrentPage}/>
      </div>
    </>
  );
};

export default Department;
